import { useState } from "react";
import { useArsTable } from "./ArsTableContextProvider";
import styles from "./ArsTableTree.module.scss";

export interface ArsTableTreeNodeProps<T> {
  defaultExpanded?: boolean;
  id: string;
  label: T;
  subNodes?: ArsTableTreeNodeProps<T>[];
  type: "operation" | "subgroup" | "groupingFactor" | "group" | "analysisMethod";
}

/**
 * Renders a single tree node with expandable children.
 *
 * @param props - Node properties
 * @param props.defaultExpanded - Whether the node should be expanded by default (default: true)
 * @param props.id - Unique identifier for the node
 * @param props.label - Display label for the node
 * @param props.subNodes - Optional child nodes to render under this node
 * @param props.type - Type of the node (operation, subgroup, groupingFactor, group, analysisMethod)
 * @returns Rendered tree node component
 */
export const ArsTableTreeNode = <T,>({
  defaultExpanded = true,
  id,
  label,
  subNodes,
  type,
}: ArsTableTreeNodeProps<T>) => {
  const { state } = useArsTable<T>();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  if (!state) return null;

  const hasChildren = subNodes && subNodes.length > 0;
  const stringLabel = state.settings.editorProps.getTextFromT(label).join("");

  return (
    <div className={styles["mos-ars-tree-node"]}>
      <div className={styles["mos-ars-tree-node-content"]}>
        {hasChildren ? (
          <button
            className={styles["mos-ars-tree-node-toggle"]}
            onClick={() => setIsExpanded(!isExpanded)}
            type="button"
            aria-label={`Toggle ${stringLabel}`}
          >
            {isExpanded ? "\u25bc" : "\u25b6"}
          </button>
        ) : (
          <span className={styles["mos-ars-tree-node-spacer"]} />
        )}

        <span
          className={`${styles["mos-ars-tree-node-type"]} ${
            styles[`mos-ars-tree-node-type-${type}`]
          }`}
        >
          {type}
        </span>

        <span className={styles["mos-ars-tree-node-label"]}>{stringLabel}</span>

        <span className={styles["mos-ars-tree-node-id"]}>{id}</span>
      </div>

      {hasChildren && isExpanded && (
        <div className={styles["mos-ars-tree-node-children"]}>
          {subNodes.map((child) => (
            <ArsTableTreeNode
              key={child.id}
              {...child}
              defaultExpanded={defaultExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
};

ArsTableTreeNode.displayName = "ArsTableTreeNode";
